import { Injectable } from "@angular/core";
import { OrderDataImpl, moment } from "shared/cubiq-model-multi-asset";
import { CurrencyPairCalendarLookupService } from "shared/cubiq-services/lookup/model/impl/CurrencyPairCalendarLookupService";
import { HedgeOrderDataLookupService } from "shared/cubiq-services/lookup/model/impl/HedgeOrderDataLookupService";
import { HedgeOrderDataPrimaryLookupService } from "shared/cubiq-services/lookup/model/impl/HedgeOrderDataPrimaryLookupService";
import { OrderDataLookupService } from "shared/cubiq-services/lookup/model/impl/OrderDataLookupService";
import { OrderDataPrimaryLookupService } from "shared/cubiq-services/lookup/model/impl/OrderDataPrimaryLookupService";
import { TraderGroupLookupService } from "shared/cubiq-services/lookup/model/impl/TraderGroupLookupService";
import { SysFeatures, SystemConfigPromiseService } from "shared/auth-services";
import { VolumePerMinuteHelper } from "./VolumePerMinuteHelper";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

export const DEFAULT_VOLUME_PER_MINUTE_CHECK_INTERVAL: number = 60000;

@Injectable()
export class VolumePerMinuteService extends VolumePerMinuteHelper {
  protected refreshInterval: number = DEFAULT_VOLUME_PER_MINUTE_CHECK_INTERVAL;

  protected algoOrderProcess: boolean = false;
  protected hedgeOrderProcess: boolean = false;

  constructor(
    protected orderService: OrderDataPrimaryLookupService,
    protected hedgeService: HedgeOrderDataPrimaryLookupService,
    protected systemConfig: SystemConfigPromiseService,
    protected traderGroupLookup: TraderGroupLookupService,
    protected calendarService: CurrencyPairCalendarLookupService,
  ) {
    super(traderGroupLookup, calendarService);

    this.systemConfig.getSystemConfig(SysFeatures.volPerMinUIRefreshMS).then((value: string) => {
      this.setRefreshInterval(value);
      this.initialize();
    });
  }

  protected setRefreshInterval(value: string): void {
    try {
      this.refreshInterval = Number(value);
    } catch (e) {
      logger.out.error(
        `${this.constructor.name}: Failed to parse system config for ${SysFeatures.volPerMinUIRefreshMS} using default value of ${DEFAULT_VOLUME_PER_MINUTE_CHECK_INTERVAL}ms for volume per minute refresh.`,
      );
    }

    if (isNaN(this.refreshInterval)) {
      logger.out.error(
        `${this.constructor.name}: Failed to parse system config for ${SysFeatures.volPerMinUIRefreshMS} using default value of ${DEFAULT_VOLUME_PER_MINUTE_CHECK_INTERVAL}ms for volume per minute refresh.`,
      );
      this.refreshInterval = DEFAULT_VOLUME_PER_MINUTE_CHECK_INTERVAL;
    }

    if (this.refreshInterval < 1000 || this.refreshInterval > 120000) {
      logger.out.warn(
        `${this.constructor.name}: Vol/Min interval out of bounds [${this.refreshInterval}], resetting to ${DEFAULT_VOLUME_PER_MINUTE_CHECK_INTERVAL}`,
      );
      this.refreshInterval = DEFAULT_VOLUME_PER_MINUTE_CHECK_INTERVAL;
    }

    logger.out.info(
      `${this.constructor.name}: Updating Vol/Min orders at interval: ${this.refreshInterval}`,
    );
  }

  protected initialize(): void {
    let interval: number = this.refreshInterval;

    Promise.all([this.orderService.isPrimed(), this.hedgeService.isPrimed()]).then(
      (value: [boolean, boolean]) => {
        this.algoOrderProcess = value[0];
        this.hedgeOrderProcess = value[1];

        if (this.algoOrderProcess) {
          logger.out.info(
            `${this.constructor.name}: Schedulling Volume Per Minute check task for algos, interval: ${interval}ms`,
          );
        } else {
          logger.out.info(
            `${this.constructor.name}: Order data service not primed. Volume per minute refresh for algos will not be available.`,
          );
        }

        if (this.hedgeOrderProcess) {
          logger.out.info(
            `${this.constructor.name}: Schedulling Volume Per Minute check task for hedges, interval: ${interval}ms`,
          );
        } else {
          logger.out.info(
            `${this.constructor.name}: Hedge data service not primed. Volume per minute refresh for BM algos will not be available.`,
          );
        }

        if (this.algoOrderProcess || this.hedgeOrderProcess) {
          setInterval(this.onIntervalTick.bind(this), interval);
        }
      },
    );
  }

  /**
   * Called each time the timers for updating order values is fired.
   */
  protected onIntervalTick(): void {
    VolumePerMinuteHelper.lastUpdateTime = moment().startOf("second");

    if (this.algoOrderProcess) {
      this.onOrderSchedulledIntervalTick();
    }

    if (this.hedgeOrderProcess) {
      this.onHedgeSchedulledIntervalTick();
    }
  }

  /**
   * Pulls all viable candidates for volume per minute re-calculation from
   * limits service and publishes an update on each candidate
   */
  public onOrderSchedulledIntervalTick(): void {
    try {
      let orderLookup: OrderDataLookupService = this.orderService.getBase();
      let orders: OrderDataImpl[] = orderLookup.getAll().filter(this.isSupportedOrder.bind(this));

      logger.out.info(
        `${this.constructor.name}: Found ${orders.length} algo orders that are volume per minute candidates.`,
      );

      if (orders.length > 0) {
        orders
          .map((o) => o.orderId.toString())
          .forEach((orderId) => this.orderService.onOrderFlash(orderId));
      }
    } catch (e) {
      logger.out.error(
        `${this.constructor.name}: Failed to publish update notifications for algo volume per minute candidates. ${e}`,
      );
    }
  }

  /**
   * Pulls all viable candidates for volume per minute re-calculation from
   * limits service and publishes an update on each candidate
   */
  public onHedgeSchedulledIntervalTick(): void {
    try {
      let hedgeLookup: HedgeOrderDataLookupService = this.hedgeService.getBase();
      let hedges: OrderDataImpl[] = hedgeLookup.getAll().filter(this.isSupportedOrder.bind(this));

      logger.out.info(
        `${this.constructor.name}: Found ${hedges.length} BM algo orders that are volume per minute candidates.`,
      );

      if (hedges.length > 0) {
        hedges
          .map((o) => o.orderId.toString())
          .forEach((orderId) => this.hedgeService.onOrderFlash(orderId));
      }
    } catch (e) {
      logger.out.error(
        `${this.constructor.name}: Failed to publish update notifications for BM algo volume per minute candidates. ${e}`,
      );
    }
  }
}
